import { initAnalytics, trackPage } from "common/analytics"
import NextLink from "common/bit/NextLink"
import ContinuePurchase from "components/shopFeatures/continuePurchase/ContinuePurchase"
import { ContextProviders } from "contexts/ContextProviders"
import Layout from "layout/Layout"
import { DefaultSeo } from "next-seo"
import { AppProps } from "next/app"
import dynamic from "next/dynamic"
import { Router, useRouter } from "next/router"
import { useEffect, useState } from "react"
import { hotjar } from "react-hotjar"
import { Provider } from "react-redux"
import { wrapper } from "store"
import styled from "styled-components"
import "swiper/css/bundle"
import ThemeProviders from "theme/ThemeProviders"
import colors from "theme/colors"
import "theme/global.css"

const AzureApplicationInsights = dynamic(
  () => import("common/AzureApplicationInsights"),
  { ssr: false }
)

const StyledEnvironmentName = styled.pre`
  background-color: ${colors.mazdaRed};
  color: ${colors.white};
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999999999999;
  margin: 0;
  opacity: 0.8;
  pointer-events: none;
`
const StyledDSButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 0;
  z-index: 999999999999;
  /* cool css */
  align-items: center;
  background-image: linear-gradient(144deg, #af40ff, #5b42f3 50%, #00ddeb);
  border: 0;
  border-radius: 8px;
  box-shadow: rgba(151, 65, 252, 0.2) 0 15px 30px -5px;
  box-sizing: border-box;
  color: #fff;
  display: flex;
  font-size: 16px;
  font-weight: bold;
  justify-content: center;
  line-height: 1em;
  max-width: 100%;
  min-width: 140px;
  padding: 3px;
  text-decoration: none;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
  white-space: nowrap;
  cursor: pointer;

  :active,
  :hover {
    outline: 0;
  }

  span {
    background-color: rgb(5, 6, 45);
    padding: 16px 24px;
    border-radius: 6px;
    width: 100%;
    height: 100%;
    transition: 300ms;
  }

  :hover span {
    background: none;
  }
`
const ORIGIN = process.env.ORIGIN
if (!ORIGIN) {
  throw new Error("Please provide the ORIGIN environment variable.")
}

const ENV = process.env.ENV

const HOTJAR_ID = process.env.HOTJAR_ID

Router.events.on("routeChangeComplete", (url: string) => {
  // let react router do the page tracking for the model page
  if (/^\/\d+/.test(url)) return
  trackPage()
  window.appInsights?.trackPageView()
})

const MyApp = ({ Component, ...rest }: AppProps) => {
  const { store, props } = wrapper.useWrappedStore(rest)
  const [loadAzure, setLoadAzure] = useState(false)

  useEffect(() => {
    // Remove the server-side injected CSS
    const jssStyles = document.querySelector("#jss-server-side")
    jssStyles?.parentElement?.removeChild(jssStyles)
    // initiate after page load
    const pageLoadTimer = setTimeout(() => {
      // init application insights
      setLoadAzure(true)

      // load GTM on load and no through cookies consent
      initAnalytics()
      trackPage()

      // Initialize Hotjar
      if (HOTJAR_ID) {
        hotjar.initialize(parseInt(HOTJAR_ID), 6)
      }
    }, 1000)
    return () => {
      clearTimeout(pageLoadTimer)
    }
  }, [])

  const { asPath } = useRouter()
  const url = (ORIGIN + asPath).split(/[?#]/)[0] // remove query string & hash

  return (
    <>
      <DefaultSeo
        titleTemplate="%s - mazda"
        canonical={url}
        dangerouslySetAllPagesToNoIndex={ENV !== "production"}
        dangerouslySetAllPagesToNoFollow={ENV !== "production"}
        openGraph={{
          site_name: "mazda",
          locale: "he_IL",
          type: "website",
          url,
        }}
      />
      <Provider store={store}>
        <ThemeProviders>
          <ContextProviders>
            <Layout>
              <Component {...props.pageProps} />
              <ContinuePurchase />
            </Layout>
          </ContextProviders>
        </ThemeProviders>
        {ENV && ENV !== "production" && (
          <StyledEnvironmentName>{ENV}</StyledEnvironmentName>
        )}
        {false && ENV && ENV === "development" && (
          <NextLink href="/ds" passHref>
            <StyledDSButton>
              <span>DS and Reusable</span>
            </StyledDSButton>
          </NextLink>
        )}
        {loadAzure && <AzureApplicationInsights />}
      </Provider>
    </>
  )
}

export default MyApp
